// Is this tab running the release the server is serving? (Oct 2026.)
//
// nginx keeps the shell out of every cache, so a *load* always takes the current release.
// That fixes the tab reopened after a deploy and does nothing at all for the tab that is
// never reopened — which on a kiosk is most of them. A wall-mounted terminal is loaded
// once and left on for weeks, so without something that notices, a Pi rolls forward and
// the screens in front of the staff keep running whatever was current the morning they
// were last power-cycled.
//
// The version is not a number anyone has to remember to bump. Vite names the entry chunk
// and every preloaded chunk by content hash, so the set of filenames the shell references
// IS the identity of a build: same source, same names; any change, new names. Compare the
// set this tab booted with against the set the server's `index.html` names now and you
// have asked the only question that matters — "is the JS I am running still the JS this
// node serves?" — without a build-time stamp, a version endpoint, or anything CI has to
// pass in. It is also right on every surface the bundle is served from, the Pi's nginx and
// S3 + CloudFront alike.
//
// In dev the shell names `/src/index.jsx` and `/@vite/client`, which never change, so the
// comparison is permanently equal and this quietly does nothing. HMR is the dev story.

// Every hashed thing the shell names, any attribute order. The stylesheet counts: Vite
// extracts CSS to its own content-hashed file that the JS chunk does not reference, so a
// release that only restyles something leaves the entry hash alone and would otherwise
// never reach a terminal. The constant `<link rel="preconnect">` and the Google Fonts
// URL come along too — they are part of the shell and they never change, so they cost a
// few bytes of comparison and nothing else.
const MODULE_SCRIPT = /<script\b[^>]*\btype=["']module["'][^>]*>/gi;
const MODULE_PRELOAD = /<link\b[^>]*\brel=["']modulepreload["'][^>]*>/gi;
const STYLESHEET = /<link\b[^>]*\brel=["']stylesheet["'][^>]*>/gi;
const URL_ATTR = /\b(?:src|href)=["']([^"']+)["']/i;

const collect = (html, tagPattern) => {
  const out = [];
  for (const [tag] of html.matchAll(tagPattern)) {
    const url = tag.match(URL_ATTR)?.[1];
    if (url) out.push(url);
  }
  return out;
};

/**
 * The asset URLs an HTML shell references, sorted and de-duplicated.
 *
 * A regex rather than DOMParser so the rule is pure and tested without a browser, the way
 * `idleStep` is. The shape it reads is Vite's own output, not arbitrary HTML.
 */
export function shellAssets(html) {
  if (typeof html !== 'string' || !html) return [];
  const urls = [
    ...collect(html, MODULE_SCRIPT),
    ...collect(html, MODULE_PRELOAD),
    ...collect(html, STYLESHEET),
  ];
  return [...new Set(urls)].sort();
}

/** One comparable string for a build. Empty when the shell named nothing we recognise. */
export function fingerprint(assets) {
  return (assets || []).join('\n');
}

/** The running tab's own fingerprint, read off the live document. */
export function documentFingerprint(doc) {
  const nodes = doc?.querySelectorAll?.(
    'script[type="module"][src], link[rel="modulepreload"][href], link[rel="stylesheet"][href]',
  );
  if (!nodes) return '';
  const urls = [...nodes].map((el) => el.getAttribute('src') || el.getAttribute('href')).filter(Boolean);
  return fingerprint([...new Set(urls)].sort());
}

/**
 * What to do about a fingerprint that differs from the one this tab booted with. Pure, so
 * the policy is testable without a browser or a clock.
 *
 * An automatic reload is only allowed where it is genuinely free, and "free" turned out to
 * be a narrower claim than this first said (#308 review). `orderDraft` mirrors the unsent
 * cart to localStorage, which is what makes the idle *lock* non-destructive — but it is the
 * cart and nothing else. The payment dialog's `stage`, `tendered`, `lastChange`, the tip
 * choice and the receipt address are plain component state, and twenty seconds without a
 * tap is an ordinary pause there: a server counting change out of the drawer, a guest
 * finding their card. So a reload at that moment does not cost a repaint, it costs the
 * change-due figure they were reading.
 *
 * `busy` is that screen, and the kitchen and bar boards as well — see `lib/reloadSafety`.
 * It is checked before `hidden`, because a payment dialog left open on a tab that is no
 * longer in front is exactly the state worth keeping.
 *
 *   `wait`    nothing new.
 *   `reload`  nobody is looking (hidden) or nobody is touching it (idle), and nothing on
 *             the screen would be lost — take it now.
 *   `prompt`  offer it and ask again on the next tick. Also where we have already reloaded
 *             once for this exact version and it is *still* being offered: something is
 *             wrong (a half-rolled deploy, a proxy of someone's own), and reloading a till
 *             in a loop is worse than leaving it one release behind — but going silent
 *             leaves it stale with nothing on screen to say so, which is the very thing
 *             this exists to prevent. The hook prompts once per tab, so offering here
 *             carries no loop risk.
 */
export function reloadDecision({ changed, alreadyTried, busy, hidden, msSinceActivity, idleMs }) {
  if (!changed) return 'wait';
  if (alreadyTried) return 'prompt';
  if (busy) return 'prompt';
  if (hidden) return 'reload';
  return msSinceActivity >= idleMs ? 'reload' : 'prompt';
}
